import { useState } from 'react'

const AdvancedConnectionTest = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [debugInfo, setDebugInfo] = useState({})

  // Enhanced debugging function
  const testConnectionAdvanced = async (url, label, options = {}) => {
    setLoading(true)
    const startTime = Date.now()
    
    try {
      console.group(`🔍 Advanced Test: ${label}`)
      console.log('URL:', url)
      console.log('Options:', options)
      console.log('Start time:', new Date().toISOString())
      
      // Check if URL is reachable using different methods
      const debugResults = {
        url,
        label,
        timestamp: new Date().toLocaleString(),
        browserInfo: {
          userAgent: navigator.userAgent,
          onLine: navigator.onLine,
          protocol: window.location.protocol,
          hostname: window.location.hostname,
          port: window.location.port
        },
        attempts: []
      }

      // Attempt 1: Basic fetch with timeout
      try {
        console.log('🔗 Attempt 1: Basic fetch with 10s timeout')
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 10000)
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          signal: controller.signal,
          ...options
        })
        
        clearTimeout(timeoutId)
        const responseTime = Date.now() - startTime
        const data = await response.text()
        
        debugResults.attempts.push({
          method: 'Basic fetch',
          success: true,
          status: response.status,
          statusText: response.statusText,
          responseTime: `${responseTime}ms`,
          headers: Object.fromEntries(response.headers.entries()),
          data: data.substring(0, 500) + (data.length > 500 ? '...' : '')
        })
        
        console.log('✅ Success:', response.status, responseTime + 'ms')
        console.groupEnd()
        
        setResults(prev => [...prev, {
          ...debugResults,
          status: response.status,
          success: response.ok,
          data: data,
          responseTime: `${responseTime}ms`,
          mainResult: 'SUCCESS'
        }])
        
        setLoading(false)
        return
        
      } catch (fetchError) {
        console.log('❌ Basic fetch failed:', fetchError.message)
        debugResults.attempts.push({
          method: 'Basic fetch',
          success: false,
          error: fetchError.message,
          errorType: fetchError.name,
          responseTime: `${Date.now() - startTime}ms`
        })
      }

      // Attempt 2: Fetch with no-cors mode
      try {
        console.log('🔗 Attempt 2: No-CORS mode')
        const response = await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          ...options
        })
        
        const responseTime = Date.now() - startTime
        console.log('🤔 No-CORS response (opaque):', response.type, response.status)
        
        debugResults.attempts.push({
          method: 'No-CORS fetch',
          success: response.type === 'opaque',
          status: response.status,
          type: response.type,
          responseTime: `${responseTime}ms`,
          note: 'Opaque response - server reachable but CORS blocked'
        })
        
      } catch (noCorsError) {
        console.log('❌ No-CORS failed:', noCorsError.message)
        debugResults.attempts.push({
          method: 'No-CORS fetch',
          success: false,
          error: noCorsError.message,
          errorType: noCorsError.name
        })
      }

      // Attempt 3: XMLHttpRequest
      try {
        console.log('🔗 Attempt 3: XMLHttpRequest')
        
        const xhr = new XMLHttpRequest()
        const xhrPromise = new Promise((resolve, reject) => {
          xhr.timeout = 10000
          
          xhr.onload = () => {
            resolve({
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText,
              readyState: xhr.readyState
            })
          }
          
          xhr.onerror = () => reject(new Error('XHR Network Error'))
          xhr.ontimeout = () => reject(new Error('XHR Timeout'))
          xhr.onabort = () => reject(new Error('XHR Aborted'))
        })
        
        xhr.open('GET', url, true)
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.send()
        
        const xhrResult = await xhrPromise
        const responseTime = Date.now() - startTime
        
        console.log('✅ XHR Success:', xhrResult.status)
        
        debugResults.attempts.push({
          method: 'XMLHttpRequest',
          success: true,
          status: xhrResult.status,
          statusText: xhrResult.statusText,
          responseTime: `${responseTime}ms`,
          readyState: xhrResult.readyState,
          data: xhrResult.responseText.substring(0, 500)
        })
        
      } catch (xhrError) {
        console.log('❌ XHR failed:', xhrError.message)
        debugResults.attempts.push({
          method: 'XMLHttpRequest',
          success: false,
          error: xhrError.message,
          responseTime: `${Date.now() - startTime}ms`
        })
      }

      // Network connectivity tests
      try {
        console.log('🔗 Attempt 4: Image load test (different protocol)')
        
        const img = new Image()
        const imgPromise = new Promise((resolve, reject) => {
          img.onload = () => resolve('Image loaded successfully')
          img.onerror = () => reject(new Error('Image load failed'))
          img.src = url.replace(/:\d+/, ':5000') + '/favicon.ico?' + Date.now()
        })
        
        setTimeout(() => imgPromise.catch(() => {}), 5000) // Timeout after 5s
        
        try {
          await imgPromise
          debugResults.attempts.push({
            method: 'Image load test',
            success: true,
            note: 'Server responding to requests'
          })
        } catch (imgError) {
          debugResults.attempts.push({
            method: 'Image load test',
            success: false,
            error: imgError.message
          })
        }
      } catch (imgTestError) {
        console.log('Image test error:', imgTestError)
      }

      console.groupEnd()
      
      // Final result compilation
      const hasAnySuccess = debugResults.attempts.some(attempt => attempt.success)
      const totalTime = Date.now() - startTime
      
      setResults(prev => [...prev, {
        ...debugResults,
        status: hasAnySuccess ? 'PARTIAL' : 'FAILED',
        success: hasAnySuccess,
        data: `Advanced debugging completed. ${debugResults.attempts.length} attempts made.`,
        responseTime: `${totalTime}ms`,
        mainResult: hasAnySuccess ? 'PARTIAL SUCCESS' : 'ALL FAILED'
      }])

    } catch (error) {
      console.error('❌ Advanced test failed:', error)
      console.groupEnd()
      
      setResults(prev => [...prev, {
        label,
        url,
        status: 'ERROR',
        success: false,
        data: `Advanced test error: ${error.message}`,
        timestamp: new Date().toLocaleString(),
        mainResult: 'CRITICAL ERROR'
      }])
    }
    
    setLoading(false)
  }

  // Network diagnostics
  const runNetworkDiagnostics = async () => {
    setLoading(true)
    
    const diagnostics = {
      label: 'Network Diagnostics',
      timestamp: new Date().toLocaleString(),
      tests: []
    }

    // Test 1: Check if localhost resolves
    try {
      const localhostTest = await fetch('http://localhost:3000/api-test', { mode: 'no-cors' })
      diagnostics.tests.push({
        test: 'Localhost frontend connectivity',
        result: 'SUCCESS - Frontend is accessible',
        details: `Status: ${localhostTest.status}, Type: ${localhostTest.type}`
      })
    } catch (error) {
      diagnostics.tests.push({
        test: 'Localhost frontend connectivity',
        result: 'FAILED',
        details: error.message
      })
    }

    // Test 2: Browser capabilities
    diagnostics.tests.push({
      test: 'Browser capabilities',
      result: 'INFO',
      details: {
        fetch: typeof fetch !== 'undefined',
        xhr: typeof XMLHttpRequest !== 'undefined',
        webSockets: typeof WebSocket !== 'undefined',
        serviceWorker: 'serviceWorker' in navigator,
        onLine: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled
      }
    })

    // Test 3: CORS preflight simulation
    diagnostics.tests.push({
      test: 'CORS Configuration Check',
      result: 'INFO',
      details: {
        origin: window.location.origin,
        expectedBackend: 'http://localhost:5000',
        note: 'Backend should allow origin: ' + window.location.origin
      }
    })

    setResults(prev => [...prev, {
      ...diagnostics,
      status: 'INFO',
      success: true,
      data: JSON.stringify(diagnostics.tests, null, 2),
      mainResult: 'DIAGNOSTICS'
    }])

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">🔍 Advanced Connection Debugger</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Advanced Network Tests</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button 
              onClick={() => testConnectionAdvanced('http://localhost:5000/', 'Server Root Advanced')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              disabled={loading}
            >
              🔍 Deep Test: Server Root
            </button>
            
            <button 
              onClick={() => testConnectionAdvanced('http://localhost:5000/api/health', 'Health Check Advanced')}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
              disabled={loading}
            >
              🏥 Deep Test: Health Check
            </button>
            
            <button 
              onClick={() => testConnectionAdvanced('http://127.0.0.1:5000/', 'IP Address Test')}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              disabled={loading}
            >
              🌐 Test: Direct IP
            </button>
            
            <button 
              onClick={runNetworkDiagnostics}
              className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
              disabled={loading}
            >
              🩺 Network Diagnostics
            </button>
            
            <button 
              onClick={() => setResults([])}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              🗑️ Clear Results
            </button>

            <button 
              onClick={() => console.clear()}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              🧹 Clear Console
            </button>
          </div>
          {loading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
              <p className="text-blue-600">🔄 Running advanced network tests... Check browser console for detailed logs.</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">🔬 Detailed Test Results</h2>
          {results.length === 0 ? (
            <p className="text-gray-500">No tests run yet. Click a button above to start advanced debugging.</p>
          ) : (
            <div className="space-y-6">
              {results.map((result, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{result.label}</h3>
                      <p className="text-sm text-gray-600">{result.timestamp}</p>
                      <p className="text-sm font-medium">Result: {result.mainResult}</p>
                    </div>
                    <div className={`px-3 py-1 rounded text-sm font-medium ${
                      result.success 
                        ? 'bg-green-100 text-green-800' 
                        : result.mainResult === 'PARTIAL SUCCESS'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {result.status}
                    </div>
                  </div>
                  
                  {result.attempts && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Attempt Details:</h4>
                      <div className="space-y-2">
                        {result.attempts.map((attempt, idx) => (
                          <div key={idx} className={`p-3 rounded border-l-4 ${
                            attempt.success ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
                          }`}>
                            <div className="flex justify-between items-start">
                              <span className="font-medium">{attempt.method}</span>
                              <span className={`text-sm ${attempt.success ? 'text-green-600' : 'text-red-600'}`}>
                                {attempt.success ? '✅' : '❌'}
                              </span>
                            </div>
                            {attempt.error && <p className="text-sm text-red-600 mt-1">Error: {attempt.error}</p>}
                            {attempt.status && <p className="text-sm mt-1">Status: {attempt.status}</p>}
                            {attempt.responseTime && <p className="text-sm mt-1">Time: {attempt.responseTime}</p>}
                            {attempt.note && <p className="text-sm text-blue-600 mt-1">Note: {attempt.note}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-gray-100 p-3 rounded text-sm font-mono max-h-96 overflow-y-auto">
                    <pre>{typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2)}</pre>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdvancedConnectionTest